/**
 * Search and the bias pills — ROADMAP 5.19.
 *
 * The pills carry counts because a pill reading zero is the answer to "do I
 * have anything bearish" without a click, and because a count that moves as
 * the search is typed shows the box is doing something on a page short
 * enough that the result might otherwise look unchanged.
 */

import { Search } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Input } from '@/components/ui/input'
import { BIAS_LABEL } from '../constants'
import { ALL_BIASES, type BiasFilter } from '../utils/strategyFilter'

export function StrategyToolbar({
  query,
  bias,
  biases,
  counts,
  onQuery,
  onBias,
}: {
  query: string
  bias: BiasFilter
  /**
   * The biases this account actually holds, in display order.
   *
   * Derived from the WHOLE list rather than from `counts`, which is counted
   * after the search. A pill that vanished as you typed would move the one
   * you were about to click, and SPECIAL — empty on most accounts — would
   * otherwise be a permanently dead pill teaching that the pills are
   * decoration. A bias emptied by the SEARCH stays, greyed, showing its
   * zero: there the zero is the answer.
   */
  biases: readonly string[]
  counts: Map<string, number>
  onQuery: (q: string) => void
  onBias: (b: BiasFilter) => void
}) {
  const pill = (value: BiasFilter, label: string, n: number) => (
    <button
      key={value}
      type="button"
      onClick={() => onBias(value)}
      aria-pressed={bias === value}
      data-testid={`bias-pill-${value}`}
      className={cn(
        'flex h-7 shrink-0 items-center gap-1.5 rounded-full px-3 text-[12px] font-medium transition-colors',
        bias === value
          ? 'bg-primary text-primary-foreground'
          : 'text-muted-foreground hover:bg-accent hover:text-foreground',
        bias !== value && n === 0 && 'opacity-45',
      )}
    >
      {label}
      <span className={cn('num text-[11px]', bias === value ? 'opacity-80' : 'opacity-70')}>
        {n}
      </span>
    </button>
  )

  return (
    <div className="flex flex-wrap items-center gap-2">
      <div className="relative min-w-[200px] max-w-[280px] flex-1">
        <Search
          size={13}
          className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground"
        />
        <Input
          value={query}
          onChange={(e) => onQuery(e.target.value)}
          placeholder="Search strategies…"
          aria-label="Search strategies"
          data-testid="strategy-search"
          className="h-8 pl-8 text-[12.5px]"
        />
      </div>

      <div className="flex flex-wrap items-center gap-1">
        {pill(ALL_BIASES, 'All', counts.get(ALL_BIASES) ?? 0)}
        {biases.map((b) => pill(b, BIAS_LABEL[b] ?? b, counts.get(b) ?? 0))}
      </div>
    </div>
  )
}
